import { saveImageBlobToDB } from "../../../IndexedDBUtils";

// One /api/images/batch call returns every thumbnail of a folder as
// { path: { base64, width, height, ... } }, so a gallery never needs one
// request per image. Shared by Home's preload and the Swingies body gallery's
// fallback, so both write the same IndexedDB store with the same keys.

// Seconds to wait before retrying a shed batch when the server named none, and
// the most any Retry-After may hold a guest back.
export const BATCH_RETRY_DEFAULT_S = 3;
export const BATCH_RETRY_MAX_S = 10;

// The server's own retry hint on a 503: the Retry-After header, else the JSON
// body's retry_after ({"error":"busy"|"cache_unavailable","retry_after":N}).
// null when it names none (an HTML body, an older server).
const readRetryAfterSeconds = async (response) => {
  const header =
    response.headers && typeof response.headers.get === "function"
      ? response.headers.get("Retry-After")
      : null;
  if (header != null && header !== "" && Number.isFinite(Number(header))) {
    return Number(header);
  }
  try {
    const body = await response.json();
    const seconds = Number(body && body.retry_after);
    return body && body.retry_after != null && Number.isFinite(seconds)
      ? seconds
      : null;
  } catch (error) {
    return null;
  }
};

// GET the batch for "<event>/<folder>". Throws on a non-OK answer (with
// `status`, and `retryAfterSeconds` on a 503) or an unreadable body.
export const fetchImageBatch = async (serverUrl, folderPath) => {
  const response = await fetch(
    `${serverUrl}/api/images/batch?folder_path=${encodeURIComponent(
      folderPath
    )}`
  );
  if (!response.ok) {
    const error = new Error(`Failed to fetch ${folderPath} images batch`);
    error.status = response.status;
    if (response.status === 503) {
      error.retryAfterSeconds = await readRetryAfterSeconds(response);
    }
    throw error;
  }
  try {
    return await response.json();
  } catch (parseError) {
    const error = new Error(`Unreadable ${folderPath} images batch`);
    error.status = response.status;
    throw error;
  }
};

// A 503 (the server shed the request with "busy", or Redis was unreachable:
// "cache_unavailable") or a network failure (no HTTP status) may pass in a few
// seconds. Anything else (404 no cache, 500, an unreadable body) will not.
export const isTransientBatchError = (error) =>
  !error || error.status == null || error.status === 503;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// fetchImageBatch, retried at most `retries` times after a transient failure,
// each time after the server's Retry-After (BATCH_RETRY_DEFAULT_S when it
// names none; clamped to 1..BATCH_RETRY_MAX_S). A shed request is retried as
// ONE request again, never split into per-file fetches. Stops (rethrowing the
// last error) once `isCurrent` turns false. `wait` is injectable for tests.
export const fetchImageBatchWithRetry = async (
  serverUrl,
  folderPath,
  { isCurrent = () => true, retries = 1, wait = sleep } = {}
) => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await fetchImageBatch(serverUrl, folderPath);
    } catch (error) {
      if (attempt >= retries || !isTransientBatchError(error) || !isCurrent()) {
        throw error;
      }
      const hinted =
        error.retryAfterSeconds != null
          ? error.retryAfterSeconds
          : BATCH_RETRY_DEFAULT_S;
      const seconds = Math.min(BATCH_RETRY_MAX_S, Math.max(1, hinted));
      await wait(seconds * 1000);
      if (!isCurrent()) throw error;
    }
  }
};

// Decode and save batch entries one at a time, in batch order. `isCurrent`
// turns false once the page/visit that asked for the batch is gone: no new
// decode or write starts after that (an IDB write already in flight only ever
// targets this event's own database). `only` (optional) limits the save to
// those paths. Returns the saved paths; decode/save errors propagate.
export const saveImageBatchToDB = async (
  eventName,
  storeName,
  imageBatch,
  { isCurrent = () => true, only = null } = {}
) => {
  const wanted = only ? new Set(only) : null;
  const saved = [];
  for (const [path, fileData] of Object.entries(imageBatch || {})) {
    if (wanted && !wanted.has(path)) continue;
    if (!isCurrent()) break;
    const { base64, width, height } = fileData;
    const imageBlob = await (
      await fetch(`data:image/webp;base64,${base64}`)
    ).blob();
    // A decode that outlived this visit must not START a new write: after
    // A -> B -> A the same-named database belongs to the newer visit.
    if (!isCurrent()) break;
    await saveImageBlobToDB(
      eventName,
      storeName,
      path,
      imageBlob,
      width,
      height
    );
    saved.push(path);
  }
  return saved;
};
